<template>
  <div style="clear: both;">
    <v-dialog v-model="loading" persistent width="300">
      <v-card dark>
        <v-card-text>
          Loading...
          <v-progress-linear indeterminate color="light-green-darken-4" striped height="10" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <template v-if="formReady">
      <div class="external-moc-form">
        <v-container fluid>
          <template v-if="flagsMessage">
            <flags-notifier :message="flagsMessage" :color="flagsMessageColor"></flags-notifier>
          </template>
          <v-form ref="mocForm" autocomplete="off">
            <v-card :color="formColor" class="px-3">
              <v-row dense>
                <v-col cols="12" lg="2">
                  <initiated-date-field :date="$filters.DateFilter(moc.dates.initiated)" readonly></initiated-date-field>
                </v-col>

                <v-col cols="12" lg="4">
                  <community-field :model-value="moc.community" readonly></community-field>
                </v-col>

                <v-col cols="12" lg="2">
                  <id-field v-model="moc.mocId" :value="moc.mocId" readonly></id-field>
                </v-col>

                <v-col cols="12" lg="4">
                  <status-field :model-value="moc.status"></status-field>
                </v-col>
              </v-row>

              <v-row dense v-if="moc.status.value == closedVoidedStatus && moc.closedVoidedReason">
                <v-col cols="12" sm="12" class="white">
                  <void-reason
                    :closedVoidedReason="moc.closedVoidedReason"
                    :mocFormStatus="moc.status.value"
                    :closedVoidUndo="moc.closedVoidUndo"
                  ></void-reason>
                </v-col>
              </v-row>

              <v-row dense v-if="moc.status.value === sentBackStatus && moc.sentBackReason && displaySentBackReadOnly">
                <v-col cols="12" sm="12" class="white">
                  <sent-back-reason
                    :sentBackReason="moc.sentBackReason"
                    :sentBackHistory="moc.sentBackHistory"
                    :roles="myRoles"
                    :mocFormStatus="moc.status.value"
                  ></sent-back-reason>
                </v-col>
              </v-row>

              <v-row dense v-if="moc.status.value === beingRevisedStatus && moc.revisionReason">
                <v-col cols="12" sm="12" class="white">
                  <being-revised-reason
                    :revisionReason="moc.revisionReason"
                    :revisionHistory="moc.revisionHistory"
                    :roles="myRoles"
                    :mocFormStatus="moc.status.value"
                  ></being-revised-reason>
                </v-col>
              </v-row>

              <v-row
                dense
                v-if="
                  (moc.status.value === changeIsInPlaceWithConsequentialActionsRequired && moc.closerDeniedReason) ||
                    (moc.status.value === temporaryChangeIsInPlaceWithConsequentialActionsRequired && moc.closerDeniedReason)
                "
              >
                <v-col cols="12" sm="12" class="white">
                  <closer-denied-reason
                    :closerDeniedReason="moc.closerDeniedReason"
                    :closerDeniedHistory="moc.closerDeniedHistory"
                    :roles="myRoles"
                    :mocFormStatus="moc.status.value"
                  ></closer-denied-reason>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12" sm="6" lg="6">
                  <people-picker
                    v-model="moc.roles.initiator"
                    :label="$t('Initiator')"
                    :user="initiator"
                    name="initiator"
                    :readonly="true"
                  ></people-picker>
                </v-col>
                <v-col cols="12" sm="6" lg="6">
                  <people-picker
                    v-model="moc.roles.coinitiator"
                    :label="$t('Co-Initiator_Readonly')"
                    :user="moc.roles.coinitiator"
                    :readonly="true"
                    ref="coinitiator"
                  ></people-picker>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12">
                  <title-field
                    v-model.trim="moc.title"
                    :roles="myRoles"
                    readonly
                    :previousFormStatus="previousFormStatus"
                    ref="title"
                    :moc="moc"
                  ></title-field>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12">
                  <change-category-field
                    v-model="moc.changeCategory"
                    :items="changeCategories"
                    :roles="myRoles"
                    readonly
                    :moc="moc"
                    :previousFormStatus="previousFormStatus"
                  ></change-category-field>
                </v-col>
              </v-row>

              <div v-show="moc.changeCategory != null">
                <v-row dense>
                  <v-col cols="12">
                    <type-field
                      v-model="moc.mocType"
                      :items="mocTypes"
                      :roles="myRoles"
                      readonly
                      :moc="moc"
                      :previousFormStatus="previousFormStatus"
                    ></type-field>
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="12">
                    <description-field
                      v-model.trim="moc.description"
                      :roles="myRoles"
                      readonly
                      :moc="moc"
                      :previousFormStatus="previousFormStatus"
                    ></description-field>
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="12">
                    <justification-field
                      v-model.trim="moc.justification"
                      :roles="myRoles"
                      readonly
                      :previousFormStatus="previousFormStatus"
                      :moc="moc"
                    ></justification-field>
                  </v-col>
                </v-row>

                <v-row dense>
                  <template v-if="!moc.dates.approvedStart">
                    <v-col cols="12" sm="4">
                      <start-date-field
                        v-model="mocStartDate"
                        :roles="myRoles"
                        :moc="moc"
                        :previousFormStatus="previousFormStatus"
                        :changeType="moc.changeType.type"
                        forceReadonly
                      ></start-date-field>
                    </v-col>
                  </template>

                  <template v-if="moc.dates.approvedStart">
                    <v-col cols="12" sm="4">
                      <approved-start-date-field
                        v-model="mocApprovedStartDate"
                        :roles="myRoles"
                        forceReadonly
                        :moc="moc"
                      ></approved-start-date-field>
                    </v-col>
                  </template>

                  <v-col cols="12" sm="4">
                    <change-type-field
                      v-model="moc.changeType"
                      :items="changeTypes"
                      :roles="myRoles"
                      :moc="moc"
                      :value="selectedChangeType"
                      forceReadonly
                      :previousFormStatus="previousFormStatus"
                    ></change-type-field>
                  </v-col>

                  <template v-if="selectedChangeTypeKey === temporaryChangeTypeKey">
                    <v-col cols="12" sm="4">
                      <valid-until-date-field
                        v-model="moc.dates.validUntil"
                        :value="moc.dates.validUntil"
                        :roles="myRoles"
                        :mocFormStatus="moc.status.value"
                        :changeType="moc.changeType.type"
                        forceReadonly
                      ></valid-until-date-field>
                    </v-col>
                  </template>
                </v-row>
                <template v-if="currentQuestions">
                  <v-row dense>
                    <v-col cols="12">
                      <tree-questions
                        :questions="currentQuestions"
                        :roles="myRoles"
                        :moc="moc"
                        :mocId="moc.mocId"
                        forceReadOnly
                        :previousFormStatus="previousFormStatus"
                      ></tree-questions>
                    </v-col>
                  </v-row>
                </template>
              </div>
            </v-card>

            <div v-show="moc.changeCategory != null">
              <v-row dense class="mt-3">
                <v-col cols="12">
                  <supporting-documents :moc="moc" :showOpenText="openTextEnabled" readonly></supporting-documents>
                </v-col>
              </v-row>
              <v-row dense class="mt-3">
                <v-col cols="12">
                  <supporting-links :moc="moc" readonly></supporting-links>
                </v-col>
              </v-row>

              <template v-if="displayTasks">
                <v-row dense class="mt-3">
                  <v-col cols="12">
                    <v-card>
                      <all-tasks
                        :moc="moc"
                        :tasks="tasks"
                        :roles="myRoles"
                        :readonly="true"
                        :previousFormStatus="previousFormStatus"
                        :show="showTasks"
                      ></all-tasks
                    ></v-card>
                  </v-col>
                </v-row>
              </template>

              <template v-if="displayFA">
                <v-card :color="faBackgroundColor" class="mt-3">
                  <div>
                    <v-container fluid>
                      <template v-if="moc.multiPassCurrentPass === 0">
                        <v-row>
                          <v-col cols="12" sm="6">
                            <people-picker-ms
                              :label="$t('Preset_Approvers')"
                              :title="$t('Preset_Approvers')"
                              :readonly="true"
                              :showCounter="true"
                              ref="PresetApproversPeoplePicker"
                              :color="faComponentColor"
                            ></people-picker-ms>
                          </v-col>

                          <v-col cols="12" sm="6">
                            <people-picker-ms
                              :label="$t('Preset_FYIs')"
                              :title="$t('Preset_FYIs')"
                              :readonly="true"
                              :showCounter="true"
                              ref="PresetFyisPeoplePicker"
                              :color="faComponentColor"
                            ></people-picker-ms>
                          </v-col>
                        </v-row>

                        <!-- additionals -->
                        <v-row>
                          <v-col cols="12" sm="6">
                            <people-picker-ms
                              :label="$t('Additional_Approvers')"
                              v-model="moc.roles.additionalApprovers"
                              :title="$t('Additional_Approvers')"
                              readonly
                              ref="AdditionalApproversPeoplePicker"
                              :color="faComponentColor"
                            ></people-picker-ms>
                          </v-col>

                          <v-col cols="12" sm="6">
                            <people-picker-ms
                              :label="$t('Additional_FYI')"
                              v-model="moc.roles.additionalFyis"
                              :title="$t('Additional_FYI')"
                              :readonly="true"
                              ref="AdditionalFyisPeoplePicker"
                              :color="faComponentColor"
                            ></people-picker-ms>
                          </v-col>
                        </v-row>
                      </template>

                      <template v-else>
                        <v-row>
                          <v-expansion-panels accordion class="mx-3">
                            <v-expansion-panel>
                              <v-expansion-panel-title
                                disable-icon-rotate
                                @click="showOriginalDecision = !showOriginalDecision"
                                :color="faComponentColor"
                                >{{ $t('Original_Decision') }}
                                <template v-slot:actions>
                                  <v-icon>{{ showOriginalDecision ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                                </template>
                              </v-expansion-panel-title>
                              <v-expansion-panel-text :class="faBackgroundColor">
                                <v-row>
                                  <v-col cols="12" sm="6">
                                    <people-picker-ms
                                      :label="$t('Preset_Approvers')"
                                      v-model="moc.roles.approvers"
                                      :title="$t('Preset_Approvers')"
                                      :readonly="true"
                                      :showCounter="true"
                                      ref="PresetApproversPeoplePicker"
                                      :color="faComponentColor"
                                    ></people-picker-ms>
                                  </v-col>

                                  <v-col cols="12" sm="6">
                                    <people-picker-ms
                                      :label="$t('Preset_FYIs')"
                                      v-model="moc.roles.fyis"
                                      :title="$t('Preset_FYIs')"
                                      :readonly="true"
                                      :showCounter="true"
                                      ref="PresetFyisPeoplePicker"
                                      :color="faComponentColor"
                                    ></people-picker-ms>
                                  </v-col>
                                </v-row>

                                <v-row>
                                  <v-col cols="12" sm="6">
                                    <people-picker-ms
                                      :label="$t('Additional_Approvers')"
                                      v-model="moc.roles.additionalApprovers"
                                      :title="$t('Additional_Approvers')"
                                      :readonly="true"
                                      ref="AdditionalApproversPeoplePicker"
                                      :color="faComponentColor"
                                    ></people-picker-ms>
                                  </v-col>

                                  <v-col cols="12" sm="6">
                                    <people-picker-ms
                                      :label="$t('Additional_FYI')"
                                      v-model="moc.roles.additionalFyis"
                                      :title="$t('Additional_FYI')"
                                      :readonly="true"
                                      ref="AdditionalFyisPeoplePicker"
                                      :color="faComponentColor"
                                    ></people-picker-ms>
                                  </v-col>
                                </v-row>
                              </v-expansion-panel-text>
                            </v-expansion-panel>
                          </v-expansion-panels>
                        </v-row>
                      </template>
                    </v-container>
                    <!-- multipass, lets display the approvers for the current and past passes -->
                    <template v-if="displayMultipassApprovers">
                      <multipass-approvers :moc="moc" forceReadonly></multipass-approvers>
                    </template>
                    <!-- / mulitpass approvers -->
                  </div>
                </v-card>
              </template>

              <!-- actions for facilitator -->
              <v-row dense class="mt-3">
                <v-col cols="12">
                  <facilitator-actions
                    :approvalProcessType="moc.approvalProcessType"
                    :approvalProcessTypes="approvalProcessTypes"
                    :roles="myRoles"
                    :mocFormStatus="moc.status.value"
                    :moc="moc"
                    :mocOwner="moc.roles.owner"
                    forceReadonly
                  ></facilitator-actions>
                </v-col>
              </v-row>

              <v-row dense class="mt-3">
                <v-col cols="12" sm="10">
                  <processing-comments-field
                    v-model="moc.additionalInformation"
                    :value="moc.additionalInformation"
                    :roles="myRoles"
                    :mocFormStatus="moc.status.value"
                    :previousFormStatus="previousFormStatus"
                  ></processing-comments-field>
                </v-col>
              </v-row>

              <v-row
                dense
                class="mt-3"
                v-if="
                  moc.status.value == submittedStatus &&
                    moc.sentBackHistory &&
                    moc.sentBackHistory.length > 0 &&
                    displayFacilitatorActions
                "
              >
                <v-col cols="12" sm="10" class="mt-3">
                  <sent-back-reason
                    :sentBackReason="lastSentBackHistoryItem"
                    :sentBackHistory="moc.sentBackHistory"
                    :roles="myRoles"
                    :mocFormStatus="moc.status.value"
                  ></sent-back-reason>
                </v-col>
              </v-row>

              <!-- approvals --->
              <v-row dense v-if="displayApprovals" class="mt-3">
                <v-col cols="12">
                  <approvals
                    :moc="moc"
                    :showHistory="
                      displayApprovals || (moc.flags || []).includes('unauthorized') || (moc.flags || []).includes('multipass')
                    "
                    :bgColor="faBackgroundColor"
                  ></approvals>
                </v-col>
              </v-row>

              <!-- This is just a placehold for discussion, not sure where the fire is to have to add this now -->
              <v-row dense v-if="displayDiscussion" class="mt-3">
                <v-col cols="12">
                  <comments
                    :readonly="readonlyDiscussion"
                    :mocId="moc.mocId"
                    :showToast="toaster.pop"
                    :initialDiscussionDisplay="initialDiscussionDisplay"
                  ></comments>
                </v-col>
              </v-row>

              <!-- owner decision -->
              <v-row dense v-if="moc.ownerDecision && (moc.ownerDecision.decision || {}).key" class="mt-3">
                <v-col cols="12">
                  <owner-decision :ownerDecision="moc.ownerDecision" :moc="moc"></owner-decision>
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-container>
      </div>
    </template>

    <v-dialog v-model="unauthorizedExpiredGeneralPopupShow" persistent max-width="900">
      <v-card>
        <v-system-bar color="error" height="40" window>
          <span>{{ $t('This_Moc_Is_Unauthorized_Expired') }}</span>
          <v-spacer></v-spacer>
          <v-btn @click="unauthorizedExpiredGeneralPopupShow = false" icon>
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
        </v-system-bar>
        <v-card-text>
          <v-container>{{ $t('Unauthorized_Expired_Message_External_General') }}</v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="unauthorizedExpiredGeneralPopupShow = false" icon>{{ $t('OK') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <toaster ref="toaster"></toaster>
    <loading-dialog ref="loadingDialog" :mocId="this.moc.mocId"></loading-dialog>
  </div>
</template>

<script>
import { FormStatus } from '@/common'
import PeoplePicker from '@/components/peoplePicker/PeoplePicker'
import PeoplePickerMs from '@/components/peoplePicker/PeoplePickerMs'
import TreeQuestions from '@/components/TreeQuestions'
import Toaster from '@/components/Toaster'
import { mapState } from 'vuex'
import ExternalService from '@/services/external.service'
import { multiPropertySort } from '@/common/arrayFunctions'
import { ChangeTypes } from '@/common/types'

import InitiatedDateField from '@/components/mocForm/InitiatedDateField'
import CommunityField from '@/components/mocForm/CommunityField'
import IdField from '@/components/mocForm/IdField'
import TitleField from '@/components/mocForm/TitleField'
import ChangeCategoryField from '@/components/mocForm/ChangeCategoryField'
import StatusField from '@/components/mocForm/StatusField'
import TypeField from '@/components/mocForm/TypeField'
import DescriptionField from '@/components/mocForm/DescriptionField'
import JustificationField from '@/components/mocForm/JustificationField'
import StartDateField from '@/components/mocForm/StartDateField'
import ChangeTypeField from '@/components/mocForm/ChangeTypeField'
import ValidUntilDateField from '@/components/mocForm/ValidUntilDateField'
import ProcessingCommentsField from '@/components/mocForm/ProcessingCommentsField'
import FacilitatorActions from '@/components/mocForm/FacilitatorActions'
import SentBackReason from '@/components/mocForm/SentBackReason'
import Approvals from '@/components/externalMocForm/Approvals'
import ApprovedStartDateField from '@/components/mocForm/ApprovedStartDateField'
import AllTasks from '@/components/externalMocForm/AllTasks'
import i18next from 'i18next'
import VoidReason from '@/components/mocForm/VoidReason'
import OwnerDecision from '@/components/mocForm/OwnerDecision'
import CloserDeniedReason from '@/components/mocForm/CloserDeniedReason'
import SupportingDocuments from '@/components/externalMocForm/SupportingDocuments'
import SupportingLinks from '@/components/externalMocForm/SupportingLinks'
import Comments from '@/components/externalMocForm/Comments'
import { Flags } from '@/common/flags'
import MultipassApprovers from '@/components/mocForm/MultipassApprovers'
import BeingRevisedReason from '@/components/mocForm/BeingRevisedReason'
import FlagsNotifier from '@/components/mocForm/FlagsNotifier'
import LoadingDialog from '@/components/externalMocForm/LoadingDialog'

export default {
  name: 'ExternalMocForm',
  components: {
    PeoplePicker,
    TreeQuestions,
    PeoplePickerMs,
    InitiatedDateField,
    CommunityField,
    IdField,
    TitleField,
    ChangeCategoryField,
    StatusField,
    TypeField,
    DescriptionField,
    JustificationField,
    StartDateField,
    ChangeTypeField,
    ValidUntilDateField,
    ProcessingCommentsField,
    SentBackReason,
    Approvals,
    ApprovedStartDateField,
    AllTasks,
    VoidReason,
    Comments,
    Toaster,
    OwnerDecision,
    CloserDeniedReason,
    SupportingDocuments,
    SupportingLinks,
    FacilitatorActions,
    MultipassApprovers,
    BeingRevisedReason,
    FlagsNotifier,
    LoadingDialog
  },
  data: () => ({
    // validation
    formSettings: {
      mocForm: true,
      // for calendars
      startDateMenu: false,
      validUntilDateMenu: false,
      minStartDate: new Date().toISOString().split('T')[0],
      toast: {
        snackbar: false,
        color: '',
        mode: '',
        successTimeout: 2000,
        timeout: 6000,
        text: '',
        icon: null,
        closeIcon: 'close'
      }
    },
    changeCategories: [],
    loaded: false,
    moc: {
      changeType: {},
      status: {},
      roles: {},
      dates: {},
      communityData: {}
    },
    myRoles: [],
    openTextEnabled: false,
    tasks: [],
    supportingDocuments: [],
    supportingLinks: [],
    tasksForCurrentStatus: [],
    unauthorizedExpiredMessage: null,
    unauthorizedExpiredDialog: false,
    statusMessage: null,
    temporaryActionsShow: false,
    currentUser: null,
    previousFormStatus: {},
    showOriginalDecision: false,
    flagsMessage: null,
    flagsMessageColor: 'error',
    unauthorizedExpiredGeneralPopupShow: false,
    readonlyAdditionalFyis: false
  }),
  props: {
    id: {
      type: String,
      default: null
    }
  },
  methods: {
    toaster() {
      return this.$refs.toaster
    },
    setLoaded: function(val) {
      this.loaded = val
    },
    getTasksFromId: function(taskIds) {
      let results = this.moc.communityData.tasks.filter(t => {
        return taskIds.some(i => {
          return i === t.id
        })
      })

      return results
    },
    fetchMoc(communityAbbrev, id) {
      this.setLoaded(false)
      return ExternalService.getMoc(communityAbbrev, id)
        .then(data => {
          this.moc = data.item1
          this.myRoles = data.item2
        })
        .catch(e => {
          if (e.response.status === 401) {
            return
          }
          if (e.response.status === 403) {
            this.$refs.loadingDialog.error({
              message: this.$t('Error_ExternalUserForbidden_MOC', { mocId: id })
            })
            setTimeout(() => this.$router.replace('/external'), 2000)
            throw e
          }

          this.$refs.loadingDialog.error({
            message: this.$t('Error_MOCForm', { mocId: id })
          })
        })
        .finally(() => {
          this.setLoaded(true)
        })
    },
    displayDateFormat: date => {
      return date.substr(0, 10)
    },
    loadTasks: function() {
      return ExternalService.getAllTasksForMoc(this.id)
        .then(data => {
          this.tasks = data
        })
        .catch(e => {
          console.log(e)
        })
    },
    setChangeCategories: function() {
      this.changeCategories = (this.moc.communityData.changeCategories || []).filter(cg => cg.enabled)
    },
    loadForm: function() {
      this.setChangeCategories()
      this.loadTasks()
      this.$store.dispatch('currentMoc/setFormColor', 'backgroundNormal')

      if ((this.moc.flags || []).length > 0) {
        let message = ''

        if (this.moc.flags.includes(Flags.properties[Flags.MULTIPASS].key)) {
          if (this.moc.multiPassCurrentPass > 0) {
            this.flagsMessage = this.moc.approvalProcessType.multiPass.approvalPasses[this.moc.multiPassCurrentPass - 1].label
            this.flagsMessageColor = 'indigo-darken-4'
          }
        } else {
          this.moc.flags.forEach(f => {
            message = message.concat(Flags.properties[f].value).concat(' - ')
          })
          this.flagsMessage = message.slice(0, -3)
        }
      }

      if (
        this.moc.status.value === FormStatus.properties[FormStatus.SUBMITTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value ||
        this.moc.status.value ===
          FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value
      ) {
        if (this.moc.changeType.type.key === ChangeTypes.properties[ChangeTypes.TEMPORARY].key) {
          if (this.moc.flags && this.moc.flags.includes(Flags.properties[Flags.UNAUTHORIZED].key)) {
            this.unauthorizedExpiredGeneralPopupShow = true
            // difference of two dates, if validUntilHold date exists and flag extend
            let dateDiff = Date.now() - new Date(this.moc.dates.validUntil)

            if (this.moc.flags.includes('extend') && this.moc.dates.validUntilHold) {
              dateDiff = Date.now() - new Date(this.moc.dates.validUntilHold)
            }

            // days between two dates
            let nDays = dateDiff / (1000 * 3600 * 24)
            let offset = this.moc.changeType.daysInterval ? this.moc.changeType.daysInterval : 0

            if (offset > 0) {
              if (Math.floor(nDays / offset) >= 0 && Math.floor(nDays / offset) < 1) {
                this.$store.dispatch('currentMoc/setFormColor', 'backgroundUnauthorizedExpired')
                this.flagsMessageColor = 'red-lighten-3'
              } else if (Math.floor(nDays / offset) >= 1 && Math.floor(nDays / offset) < 2) {
                this.$store.dispatch('currentMoc/setFormColor', 'backgroundUnauthorizedExpired2')
                this.flagsMessageColor = 'red-accent-4'
              } else if (Math.floor(nDays / offset) >= 2) {
                this.$store.dispatch('currentMoc/setFormColor', 'backgroundUnauthorizedExpired3')
                this.flagsMessageColor = 'purple-accent-4'
              }
            }
          }
        }
      }
    },
    reloadForm: function() {
      this.$router.go()
    },
    loadDisplayNames(communityAbbrev) {
      let displayNames = {}
      this.isOverridingDisplayNames = false

      // check for overrides...
      let overrides = this.$store.state.overrides.communityOverrides
      if (overrides.find(o => o.communityAbbreviation === communityAbbrev)) {
        // there's an override, just load defaults.
        displayNames = JSON.parse(JSON.stringify(i18next.getResourceBundle('en')))
        i18next.addResourceBundle('en', communityAbbrev, displayNames, false, true)
        i18next.setDefaultNamespace(communityAbbrev)
        this.isOverridingDisplayNames = true
      } else {
        ExternalService.getDisplayNames(communityAbbrev)
          .then(data => {
            displayNames = data
          })
          .catch(e => {
            console.log(e)
          })
          .finally(() => {
            i18next.addResourceBundle('en', communityAbbrev, displayNames, false, true)
            i18next.setDefaultNamespace(communityAbbrev)
          })
      }
    }
  },
  computed: {
    ...mapState('currentMoc', ['mocid', 'mocStatus', 'formColor']),
    beingRevisedStatus: function() {
      return FormStatus.properties[FormStatus.BEINGREVISED].value
    },
    displayMultipassApprovers: function() {
      return this.isMultipass && this.moc.multiPassCurrentPass > 0
    },
    isMultipass: function() {
      return (this.moc.flags || []).includes(Flags.properties[Flags.MULTIPASS].key)
    },
    faComponentColor() {
      return !this.readonlyAdditionalFyis &&
        (!this.moc.flags.includes(Flags.properties[Flags.MULTIPASS].key) ||
          (this.moc.flags.includes(Flags.properties[Flags.MULTIPASS].key) && this.moc.multiPassCurrentPass === 0))
        ? 'activePeoplePicker'
        : 'disabledPeoplePicker'
    },
    showTasks: function() {
      if (
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value
      ) {
        return false
      }

      return true
    },
    temporaryChangeIsInPlaceWithConsequentialActionsRequired: function() {
      return FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value
    },
    temporaryChangeTypeKey: function() {
      return ChangeTypes.TEMPORARY.toString()
    },
    mocStatusName() {
      if (!this.moc) return
      return this.moc.status.value ? FormStatus.properties[this.moc.status.value].name : undefined
    },
    currentQuestions: function() {
      if (this.moc) {
        if (this.moc.status.value === FormStatus.properties[FormStatus.NEW].value) {
          if ((this.moc.communityData.questions || []).length < 1) {
            return null
          }
          return [...this.moc.communityData.questions].sort((a, b) => a.order - b.order)
        }

        if (this.moc.answeredQuestions) {
          return this.moc.answeredQuestions
        }
      }
      return null
    },
    faBackgroundColor() {
      return !this.readonlyAdditionalFyis ? 'backgroundNormal' : 'backgroundReadOnly'
    },
    formColor() {
      return this.$store.state.currentMoc.formColor ? this.$store.state.currentMoc.formColor : 'white'
    },
    showTaskLinkOnly: function() {
      switch (this.moc.status.value) {
        case FormStatus.properties[FormStatus.NEW].value:
        case FormStatus.properties[FormStatus.DRAFT].value:
        case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value:
        case FormStatus.properties[FormStatus.SUBMITTED].value:
        case FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value:
        case FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value:
        case FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value:
        case FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value: {
          return false
        }
        default:
          return true
      }
    },
    sentBackStatus: function() {
      return FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value
    },
    changeIsInPlaceWithConsequentialActionsRequired: function() {
      return FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value
    },
    closedVoidedStatus: function() {
      return FormStatus.properties[FormStatus.CLOSEDVOIDED].value
    },
    submittedStatus: function() {
      return FormStatus.properties[FormStatus.SUBMITTED].value
    },
    availableForDecisionStatus: function() {
      return FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value
    },
    newStatus: function() {
      return FormStatus.properties[FormStatus.NEW].value
    },
    pendingOwnerDecisionStatus: function() {
      return FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value
    },
    lastSentBackHistoryItem: function() {
      if (this.moc.sentBackHistory) {
        return this.moc.sentBackHistory[this.moc.sentBackHistory.length - 1]
      }
      return null
    },
    displayFA: function() {
      if (
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value &&
        (this.moc.flags || []).includes(Flags.properties[Flags.OWNEREDITABLEMAKEPERMANENT].key)
      ) {
        return true
      }

      if (
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value
      ) {
        return false
      }
      return true
    },
    displayApprovals: function() {
      if (
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value ||
        this.moc.status.value ===
          FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.OWNERAPPROVEDDEFERREDSTARTDATE].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        (this.moc.flags || []).includes(Flags.properties[Flags.EXTEND].key) ||
        (this.moc.flags || []).includes(Flags.properties[Flags.MAKEPERMANENT].key) ||
        (this.moc.flags || []).includes(Flags.properties[Flags.MULTIPASS].key)
      ) {
        return true
      }
      return false
    },
    displayFacilitatorActions: function() {
      if (
        ((this.moc.status.value === FormStatus.properties[FormStatus.NEW].value ||
          this.moc.status.value === FormStatus.properties[FormStatus.DRAFT].value ||
          this.moc.status.value === FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value) &&
          this.moc.roles.facilitator.displayName) ||
        this.moc.status.value === FormStatus.properties[FormStatus.SUBMITTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value
      ) {
        return true
      }

      return false
    },
    displaySentBackReadOnly: function() {
      if (this.moc.status.value === FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value) {
        return true
      }
      return false
    },
    displayTasks: function() {
      if (this.tasks.length > 0) {
        return true
      }
      return false
    },
    isBeingRevised: function() {
      return this.moc.status.value === FormStatus.properties[FormStatus.BEINGREVISED].value
    },

    isSentBack: function() {
      return this.moc.status.value === FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value
    },
    isClosed: function() {
      return (
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value
      )
    },
    isDraft: function() {
      return this.moc.status.value === FormStatus.properties[FormStatus.DRAFT].value
    },
    isNew: function() {
      return this.moc.status.value === FormStatus.properties[FormStatus.NEW].value
    },
    isOwnerApprovedDeferredStartDate: function() {
      return this.moc.status.value === FormStatus.properties[FormStatus.OWNERAPPROVEDDEFERREDSTARTDATE].value
    },
    requiredApprovers: function() {
      return this.moc.roles.approvers
    },
    initiator: function() {
      if (this.moc.roles.initiator) {
        return this.moc.roles.initiator
      }
      return null
    },
    readonly: function() {
      return true
    },
    mocTypes: function() {
      if (this.moc) {
        if (this.moc.changeCategory) {
          const changeCat = this.moc.changeCategory
          const index = this.$_.findIndex(this.moc.communityData.changeCategories, function(o) {
            return o.name === changeCat
          })

          if (index >= 0) {
            const disabledMocTypes = this.moc.communityData.mocTypes.filter(m => !m.enabled).map(m => m.id)
            const enabledChangeCategoryMocTypes = this.moc.communityData.changeCategories[index].mocTypes.filter(
              m => !disabledMocTypes.includes(m.id)
            )

            return enabledChangeCategoryMocTypes
          } else {
            return null
          }
        } else {
          return null
        }
      } else {
        return null
      }
    },
    approvalProcessTypes: function() {
      if (this.moc) {
        if (this.moc.changeCategory) {
          const changeCat = this.moc.changeCategory
          const index = this.$_.findIndex(this.moc.communityData.changeCategories, function(o) {
            return o.name === changeCat
          })
          if (index >= 0) {
            let sortBy = [{ prop: 'displayOrder', direction: 1 }]
            const ordered = multiPropertySort(sortBy, this.moc.communityData.changeCategories[index].approvalProcessTypes)
            return ordered
          } else {
            return null
          }
        } else {
          return null
        }
      } else {
        return null
      }
    },
    changeTypes: function() {
      if (this.moc.communityData.changeTypes) {
        let enabledChangeTypes = this.moc.communityData.changeTypes.filter(ct => {
          return ct.enabled === true
        })

        let sorted = enabledChangeTypes.sort((a, b) => (a.type.value > b.type.value ? 1 : -1))

        return sorted
      } else {
        return null
      }
    },
    communityName: function() {
      return this.moc.communityName || this.moc.communityData.communityName
    },
    formReady: function() {
      if (this.moc && this.myRoles && this.moc.mocId) {
        return true
      }
      return false
    },
    sentBackReason: function() {
      return this.moc.sentBackReason
    },
    getOwner: function() {
      if (this.moc.roles.owner) {
        return this.moc.roles.owner
      }
      return null
    },
    currentTasks: function() {
      if (this.tasks) {
        return this.tasks
      }
      return null
    },
    selectedChangeTypeKey: function() {
      if (this.moc.changeType.type) {
        return this.moc.changeType.type.key
      }
      return null
    },
    selectedChangeType: function() {
      if (this.moc.changeType.type) {
        return this.moc.changeType.type
      }
      return null
    },
    loading: function() {
      return !this.loaded
    },

    readonlyDiscussion() {
      let hasRolesInMoc = false
      Object.keys(this.moc.roles).forEach(key => {
        if (key !== 'additionalApprovers' && key !== 'approvers' && key !== 'additionalFyis' && key !== 'fyis') return
        if (hasRolesInMoc) return
        const currentRole = this.moc.roles[key]
        if (!currentRole) return
        if (Array.isArray(currentRole)) {
          const index = currentRole.findIndex(c => c.mail.toLowerCase() === (this.currentUser || {}).email.toLowerCase())
          hasRolesInMoc = index > -1
        }
      })
      return !(
        hasRolesInMoc &&
        (this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
          this.moc.status.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value)
      )
    },
    displayDiscussion() {
      return this.moc.dates.availableForDecision
    },
    initialDiscussionDisplay() {
      return (
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value
      )
    },
    mocStartDate() {
      return this.$filters.DateFilter(this.moc.dates.start)
    },
    mocApprovedStartDate() {
      return this.$filters.DateFilter(this.moc.dates.approvedStart)
    }
  },
  created() {
    if (this.$route.params.shortName) {
      this.loadDisplayNames(this.$route.params.shortName)
      this.$store.dispatch('currentMoc/setFormColor', 'backgroundNormal')
      ExternalService.isOpenTextEnabled(this.$route.params.shortName)
        .then(result => {
          this.openTextEnabled = result === true
        })
        .catch(e => {
          console.error('isOpenTextEnabled', e)
        })
      if (this.id) {
        this.fetchMoc(this.$route.params.shortName, this.id).then(() => {
          this.loadForm()
          this.$store.dispatch('currentMoc/setId', this.id)
          this.$store.dispatch('currentMoc/setStatus', this.moc.status)
        })

        ExternalService.getUserProfile()
          .then(user => (this.currentUser = user))
          .then(() => {
            this.$mocHub.mocOpened(this.id, `${this.currentUser.name} (${this.currentUser.email})`)
          })
      }
    } else {
      this.$router.replace('/external')
    }
  },
  beforeDestroy() {
    if (this.id) {
      this.$mocHub.mocClosed(this.id, `${this.currentUser.name} (${this.currentUser.email})`)
    }
  }
}
</script>

<style>
.external-moc-form .v-text-field.v-input--is-readonly > .v-input__control > .v-input__slot:after {
  border-style: none !important;
}

.external-moc-form .v-text-field.v-input--is-readonly > .v-input__control .theme--light.v-label {
  color: rgba(0, 0, 0, 0.6) !important;
}
</style>
